import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Shield, Clock, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import NewsletterSignup from '../components/NewsletterSignup';
import galleryApi from '../services/galleryApi';

// Smart image URL: converts /images/ to /api/images/ for MongoDB-backed serving
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/images/')) return '/api' + url;
  return url;
};

// Fallback handler for broken images
const handleImgError = (e) => {
  e.target.onerror = null;
  e.target.style.display = 'none';
  const parent = e.target.parentElement;
  if (parent && !parent.querySelector('.img-placeholder')) {
    const placeholder = document.createElement('div');
    placeholder.className = 'img-placeholder absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-teal-50 to-cyan-100';
    placeholder.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="text-teal-300"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg><span class="text-teal-400 text-xs mt-2 font-medium">Image unavailable</span>';
    parent.appendChild(placeholder);
  }
};

const HomePage = () => {
  const [featuredItems, setFeaturedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const items = await galleryApi.getFeaturedItems(3);
        setFeaturedItems(items);
      } catch (err) {
        console.error('Error fetching featured items:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  return (
    <div className="min-h-screen">
      <SEO
        title="Home"
        description="Discover unique antiques, fine jewellery, ceramics, and collectibles at J.B.F. Antiques & Gallery, Doncaster Corn Exchange. Browse our curated collection or visit us in person."
        path="/"
      />
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/images/hero-cornexchange.jpg)',
            filter: 'brightness(0.5)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/40" />
        
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-7xl font-serif font-bold mb-6 animate-fade-in">
            Discover Timeless Treasures
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-2xl mx-auto">
            Explore our curated collection of exquisite antiques at Doncaster Corn Exchange
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/gallery">
              <Button size="lg" className="bg-teal-700 hover:bg-teal-600 text-white px-8 py-6 text-lg">
                Explore Collection
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white hover:text-teal-800 px-8 py-6 text-lg">
                Visit Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-teal-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="text-teal-700" size={32} />
                </div>
                <h3 className="text-xl font-serif font-bold text-teal-800 mb-3">
                  Quality Selection
                </h3>
                <p className="text-gray-600">
                  Hand-picked antiques from various eras, carefully authenticated and preserved.
                </p>
              </CardContent>
            </Card>

            <Card className="border-teal-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="text-teal-700" size={32} />
                </div>
                <h3 className="text-xl font-serif font-bold text-teal-800 mb-3">
                  Expert Knowledge
                </h3>
                <p className="text-gray-600">
                  Decades of experience in antiques, providing genuine items with verified provenance.
                </p>
              </CardContent>
            </Card>

            <Card className="border-teal-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="text-teal-700" size={32} />
                </div>
                <h3 className="text-xl font-serif font-bold text-teal-800 mb-3">
                  Timeless Value
                </h3>
                <p className="text-gray-600">
                  Investment pieces that appreciate in value while adding character to your space.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Featured Items Section */}
      <section className="py-16 bg-gradient-to-b from-white to-cyan-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-serif font-bold text-teal-800 mb-4">
              Featured Antiques
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Discover our handpicked selection of exceptional pieces, each with its own story and character
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {loading ? (
              <div className="col-span-3 text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-teal-600 mx-auto" />
              </div>
            ) : featuredItems.length > 0 ? (
              featuredItems.map((item) => (
              <Card key={item.id} className="group overflow-hidden border-teal-200 hover:shadow-xl transition-all duration-300">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={getImageUrl(item.images?.length > 0 ? item.images[0] : item.image)}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={handleImgError}
                  />
                  <div className="absolute top-4 right-4 bg-teal-700 text-white px-3 py-1 rounded-full text-sm font-medium">
                    {item.year}
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-serif font-bold text-teal-800 mb-2">
                    {item.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {item.description}
                  </p>
                  <div className="flex justify-between items-center">
                    <span className="text-2xl font-bold text-teal-700">{item.price}</span>
                    <Link to="/contact">
                      <Button variant="outline" size="sm" className="border-teal-700 text-teal-700 hover:bg-teal-700 hover:text-white">
                        Inquire
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))
            ) : null}
          </div>

          <div className="text-center mt-12">
            <Link to="/gallery">
              <Button size="lg" className="bg-teal-700 hover:bg-teal-600 text-white px-8">
                View Full Gallery
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-teal-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-serif font-bold mb-6">
            Visit Our Gallery Today
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Located at the historic Doncaster Corn Exchange. 
            Open Wednesdays from 9 AM. Come explore our collection in person.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/directions">
              <Button size="lg" className="bg-white text-teal-800 hover:bg-teal-50 px-8 py-6 text-lg">
                Get Directions
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-teal-800 px-8 py-6 text-lg">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <NewsletterSignup />
    </div>
  );
};

export default HomePage;
